import {
	Alert,
	Box,
	CircularProgress,
	Divider,
	Paper,
	Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTrendDisplay } from "../hooks/useTrendDisplay";
import { filterVisibleStands } from "../utils/visibleStands";
import { CampaignBanner } from "./CampaignBanner";
import { MillVisualizer } from "./mill-visualizer/MillVisualizer";
import { useMillVisualizerData } from "./mill-visualizer/hooks/useMillVisualizerData";
import { RFactorChart } from "./RFactorChart";
import { TrendGrid } from "./TrendGrid";

export const TrendDisplay = () => {
	const { data, isLoading, error } = useTrendDisplay();
	const { data: visualizerData } = useMillVisualizerData();

	// All hooks must run on every render in the same order — this useMemo
	// MUST stay above the isLoading early return below. (React's Rules of
	// Hooks: a previous version had this after the early return and threw
	// "Rendered more hooks than during the previous render" the first time
	// data arrived and isLoading flipped to false.)
	const stands = useMemo(
		() => filterVisibleStands(data?.stands ?? []),
		[data?.stands],
	);
	const banner = data?.banner ?? null;

	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "calc(100vh - 100px)",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 1 }}>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Trend Display
			</Typography>
			<Paper elevation={3} sx={{ p: 2 }}>
				<CampaignBanner banner={banner} />

				{error ? (
					<Alert severity="error" sx={{ my: 1 }}>
						{(error as Error).message}
					</Alert>
				) : null}

				{/* 1. R-Factor chart on top */}
				<Box sx={{ my: 1 }}>
					<RFactorChart stands={stands} />
				</Box>

				{/* 2. Mill visualizer in the middle (sandwich) */}
				<Box sx={{ my: 1 }}>
					<MillVisualizer data={visualizerData} />
				</Box>

				<Divider sx={{ my: 1 }} />

				{/* 3. Property table at the bottom */}
				<Box sx={{ my: 1 }}>
					<TrendGrid stands={stands} />
				</Box>
			</Paper>
		</Box>
	);
};
